import { partial as _partial } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';
import { Button } from './Button';
import { ButtonOptionBadge } from './ButtonOptionBadge';
import { LoadingSpinner } from './LoadingSpinner';
import { booleanPropHelperFactory, conditionalHelperFactory, font, media } from './style-utils';
import { TextMarqueeProvider, TextOverflowMarquee, useTextMarquee } from './TextOverflowMarquee';
import { getThemeValue } from './Theme';

const getScopedThemeValue = _partial(getThemeValue, 'ButtonOption.Color');
const getVariantThemeValue =
  (...paths) =>
  (props) =>
    getScopedThemeValue(props.variant, ...paths)(props);

export const ButtonOption = (props) => {
  const { alignText, children, clickable, badge, loading, subtext, variant, right, ...otherProps } = props;

  // Align based on group if provided
  const textAlignment = alignText || (badge || right ? 'left' : 'center');

  const [providerProps, targetProps] = useTextMarquee();

  return (
    <TextMarqueeProvider {...providerProps}>
      <OverlayRight clickable={clickable} node={right}>
        <StyledButton variant={variant} {...otherProps} {...targetProps}>
          <Background />
          <Foreground>
            <ButtonContent alignText={textAlignment} badge={badge} subtext={subtext} variant={variant} right={right}>
              {loading !== true && children}
            </ButtonContent>
          </Foreground>
          {loading === true && <LoadingSpinner width={42} strokeWidth={6} />}
        </StyledButton>
      </OverlayRight>
    </TextMarqueeProvider>
  );
};

ButtonOption.propTypes = {
  alignText: PropTypes.oneOf(['center', 'left']),
  badge: PropTypes.node,
  children: PropTypes.node,
  clickable: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  right: PropTypes.node,
  subtext: PropTypes.node,
  variant: PropTypes.oneOf(['primary', 'secondary']),
};

ButtonOption.defaultProps = {
  clickable: true,
  variant: 'primary',
};

const ButtonContent = (props) => {
  const hasRightOverlay = props.right !== undefined;
  const hasBadge = Boolean(props.badge);
  const hasSubtext = Boolean(props.subtext);

  return (
    <ButtonContentContainer alignText={props.alignText} hasRightOverlay={hasRightOverlay}>
      <ButtonLabel alignText={props.alignText}>
        <TextOverflowMarquee>{props.children}</TextOverflowMarquee>
        {hasSubtext && (
          <Subtext>
            <TextOverflowMarquee>{props.subtext}</TextOverflowMarquee>
          </Subtext>
        )}
      </ButtonLabel>
      {hasBadge && <ButtonOptionBadge variant={props.variant}>{props.badge}</ButtonOptionBadge>}
    </ButtonContentContainer>
  );
};

const hasRightOverlay = booleanPropHelperFactory('hasRightOverlay');
const isLeftAligned = conditionalHelperFactory((props) => props.alignText === 'left');

const ButtonContentContainer = styled.div`
  ${isLeftAligned(css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 0.5rem;
    text-align: left;
  `)};

  ${hasRightOverlay(css`
    padding-right: 2.5rem;
  `)};

  & > *:first-child {
    flex: 0 1 auto;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  & + * {
    z-index: 1;
  }
`;

const Foreground = styled.div`
  width: 100%;
`;

const StyledButton = styled(Button)`
  border-radius: 2.5rem;
  color: ${getVariantThemeValue('Default.Foreground')};
  height: 5rem;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  position: relative;

  > ${Background} {
    border-radius: 2.5rem;
    background-color: ${getVariantThemeValue('Default.Background')};
  }

  :not([disabled]) {
    :active {
      transform: scale(0.94);
    }

    :focus > ${Background} {
      background-color: ${getVariantThemeValue('Hover.Background')};
    }

    @media (hover: hover) {
      :hover > ${Background} {
        background-color: ${getVariantThemeValue('Hover.Background')};
      }
    }
  }
`;

const ALIGN_BY = {
  center: 'center',
  left: 'flex-start',
};

const ButtonLabel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${(props) => ALIGN_BY[props.alignText]};

  ${font(22, 'Medium', -0.5)}

  ${media.tabletLandscapeAndUp`
    ${font(24, 'Medium', -0.5)}
  `};
`;

const Subtext = styled.span`
  ${font(11, 'Medium', -0.28)}
  margin-top: -0.2rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OverlayRight = (props) => {
  const { node, ...otherProps } = props;

  if (props.node === undefined || props.node === null) {
    return props.children;
  }

  return (
    <PositionContainer {...otherProps}>
      {props.children}
      <PositionRight>{props.node}</PositionRight>
    </PositionContainer>
  );
};

const PositionRight = styled.div`
  position: absolute;
  right: 0.6rem;
  z-index: 10;
`;

const PositionContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin: 1rem 0;

  ${StyledButton} {
    margin: 0;
  }

  > * {
    pointer-events: ${(props) => (props.clickable ? 'auto' : 'none')};
  }

  ${PositionRight} {
    pointer-events: auto;
  }
`;
